import { useMemo } from "react";
import { useWorkflowRuns, useWorkflowRunSteps } from "@/hooks/api/queries/workflows.queries";
import { useWorkflowRunStream, type StepStreamState } from "@/hooks/useWorkflowRunStream";
import type { RunStatus, StepStatus, WorkflowRunStep } from "@/types/workflow.types";
import type { WorkflowCanvasRunOverlay, WorkflowCanvasStepOverlay } from "./workflow-canvas-overlay.types";

const ACTIVE_RUN_STATUSES = new Set<RunStatus>([
  "pending",
  "running",
  "paused",
  "waiting_checkpoint",
  "paused_checkpoint",
  "paused_manual",
]);

const ACTIVE_STEP_STATUSES = new Set<StepStatus>([
  "running",
  "thinking",
  "tool_running",
  "waiting_checkpoint",
  "waiting",
  "paused",
]);

function previewText(text: string | undefined): string | undefined {
  if (!text) return undefined;
  const normalized = text.replace(/\s+/g, " ").trim();
  return normalized.length > 140 ? `${normalized.slice(-140)}` : normalized;
}

export function buildStepOverlayById(
  runSteps: WorkflowRunStep[],
  liveSteps: Record<string, StepStreamState>,
  liveTokensByStepId: Record<string, string>,
): Record<string, WorkflowCanvasStepOverlay> {
  const overlays: Record<string, WorkflowCanvasStepOverlay> = {};

  for (const step of runSteps) {
    overlays[step.stepId] = {
      status: step.status,
      progressPercent: step.progressPercent ?? 0,
      progressMessage: step.progressMessage,
      durationMs: step.durationMs,
      isActive: ACTIVE_STEP_STATUSES.has(step.status),
    };
  }

  for (const [stepId, live] of Object.entries(liveSteps)) {
    overlays[stepId] = {
      ...overlays[stepId],
      status: live.status,
      progressPercent: live.progressPercent ?? overlays[stepId]?.progressPercent ?? 0,
      progressMessage: live.progressMessage ?? overlays[stepId]?.progressMessage,
      durationMs: live.durationMs ?? overlays[stepId]?.durationMs,
      costUsd: live.costUsd ?? overlays[stepId]?.costUsd,
      liveTextPreview: previewText(liveTokensByStepId[stepId] ?? live.outputText),
      isActive: ACTIVE_STEP_STATUSES.has(live.status),
    };
  }

  return overlays;
}

export function useWorkflowCanvasRunOverlay(workflowId: string): WorkflowCanvasRunOverlay {
  const { data: runsData } = useWorkflowRuns(workflowId, { limit: 5 });
  const activeRun = useMemo(
    () => (runsData?.data ?? []).find((run) => ACTIVE_RUN_STATUSES.has(run.status)) ?? null,
    [runsData]
  );
  const runId = activeRun?.id ?? "";

  const { data: runStepsData } = useWorkflowRunSteps(runId);
  const stream = useWorkflowRunStream(runId || null, workflowId, Boolean(runId));

  const stepOverlayById = useMemo(() => {
    if (!runId) return {};
    return buildStepOverlayById(runStepsData?.data ?? [], stream.steps, stream.liveTokensByStepId);
  }, [runId, runStepsData, stream.steps, stream.liveTokensByStepId]);

  return {
    runId: activeRun?.id ?? null,
    runStatus: stream.runStatus ?? activeRun?.status ?? null,
    isConnected: stream.isConnected,
    useFallbackPolling: stream.useFallbackPolling,
    stepOverlayById,
  };
}
